import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useHashRoute } from "@/hooks/useHashRoute";
import { useBootstrapStatus } from "@/hooks/useBootstrapStatus";
import { consumeReturnTo } from "@/components/RequireAuth";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";

type Mode = "signin" | "signup";

export function Login() {
  const { loading, session } = useAuth();
  const { navigate } = useHashRoute();
  const bootstrap = useBootstrapStatus();

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (session) {
      const dest = consumeReturnTo();
      navigate(dest && dest !== "#/login" ? dest : "#/");
    }
  }, [loading, session, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup") {
        const { error: err } = await supabase.auth.signUp({
          email: email.trim().toLowerCase(),
          password,
        });
        if (err) throw err;
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });
        if (err) throw err;
      }
    } catch (e2) {
      setError(prettifyAuthError((e2 as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const valid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && password.length >= 6;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-16">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <Card className="w-full">
          <CardHeader className="items-center text-center">
            <CardTitle className="font-mono text-base">Foundry VTT</CardTitle>
            <p className="text-xs text-slate-400">on-demand server</p>
          </CardHeader>
          <Separator />
          <CardContent className="space-y-3 pt-6">
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="email" className="text-xs text-slate-400">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={busy}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="password" className="text-xs text-slate-400">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={busy}
                />
                {mode === "signup" && (
                  <p className="text-[10px] text-slate-500">At least 6 characters.</p>
                )}
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={!valid || busy}>
                {busy
                  ? "…"
                  : mode === "signup"
                    ? "Create account"
                    : "Sign in"}
              </Button>
            </form>

            <div className="pt-1 text-center text-xs text-slate-400">
              {mode === "signin" ? (
                <>
                  No account yet?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setMode("signup");
                      setError(null);
                    }}
                    className="text-slate-200 underline-offset-4 hover:underline"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have one?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setMode("signin");
                      setError(null);
                    }}
                    className="text-slate-200 underline-offset-4 hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <a
          href="#/admin"
          className="text-xs text-slate-400 underline-offset-4 hover:text-slate-200 hover:underline"
        >
          → Admin portal
        </a>

        {bootstrap.initialized === false && (
          <p className="max-w-xs text-center text-xs text-slate-500">
            First-time setup: no users are configured yet. Use the Admin portal to add the first email.
          </p>
        )}
      </div>
    </div>
  );
}

function prettifyAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("not on the allow-list") || m.includes("allow list")) {
    return "This email isn't on the allow list. Ask an admin to add it.";
  }
  if (m.includes("invalid login credentials")) return "Wrong email or password.";
  if (m.includes("user already registered")) return "An account with this email already exists. Try signing in.";
  if (m.includes("password should be at least")) return "Password is too short.";
  return message;
}
